import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import Button from "~/components-shared/Button";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { usePostHog } from "posthog-js/react";
import Card from "~/components-shared/Card";
import Icon from "~/components-shared/Icon";
import Modal from "~/components-shared/Modal";
import Alert from "~/components-shared/Alert";
import MemberCard from "~/components/MemberCard";
import { useDispatch, useSelector } from "react-redux";
import authSelectors from "~/store/auth/selectors";
import onboardingActionCreators from "~/store/onboarding/actions";
import { helpers, onboardingOrder } from "~/lib/helpers";
import { ImageCategoryEnum, IntegrationTypeEnum, MemberStatusEnum, MemberThemeEnum, StatusStateEnum } from "~/types/enums";
import Grid from "~/components-shared/Grid";
import onboardingSelectors from "~/store/onboarding/selectors";
import integrationSelectors from "~/store/integrations/selectors";
import profileOnboardingInfoSchema from "~/forms/ProfileOnboardingInfoForm/profileOnboardingInfoSchema";
import ProfileOnboardingInfoForm from "~/forms/ProfileOnboardingInfoForm";
import authActionCreators from "~/store/auth/actions";
import { Box } from "~/components-shared";
import ImageUploadControl from "../ImageUploadControl/ImageUploadControl";
const Form = styled.form`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;
const Center = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
`;
const StyledIcon = styled(Icon)`
  box-shadow: inset 0 0 0 6px ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.success.main;
}};
  border-radius: 50%;
`;
const Right = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 1em;
  min-width: 170px;
  align-items: center;
`;
const OnboardingMemberProfileCard = _ref2 => {
  let {
    open,
    ...rest
  } = _ref2;
  const {
    colors
  } = useTheme();
  const user = useSelector(authSelectors.getUser);
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const {
    t
  } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const nextStatus = useSelector(onboardingSelectors.getStatus);
  const backStatus = useSelector(onboardingSelectors.getBackStatus);
  const nextError = useSelector(onboardingSelectors.getError);
  const backError = useSelector(onboardingSelectors.getBackError);
  const availableStatuses = helpers.memberOnboardingStatuses(user.authorities);
  const statusIndex = availableStatuses.findIndex(item => item === user.status);
  const error = nextError || backError;
  const success = user?.status && onboardingOrder[user.status] > onboardingOrder[MemberStatusEnum.ONBOARDING_MEMBER_PROFILE];
  const defaultValues = {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    pronouns: user?.pronouns || "",
    position: user?.position || "",
    profileImage: user?.profileImage || "",
    slackUserId: user?.metadata.slackUserId || "",
    theme: user?.theme || MemberThemeEnum.DARK,
    department: user?.department || undefined,
    level: user?.level || undefined
  };
  const {
    handleSubmit,
    control,
    watch,
    setValue
  } = useForm({
    resolver: yupResolver(profileOnboardingInfoSchema(!!slackIntegration)),
    defaultValues
  });
  useEffect(() => {
    dispatch(authActionCreators.setTheme(watch("theme")));
  }, [watch("theme")]);
  const onBack = () => {
    dispatch(onboardingActionCreators.backStarted());
    posthog.capture(`Onboarding Member Profile – Back`);
  };
  const onSubmit = handleSubmit(data => {
    dispatch(onboardingActionCreators.memberProfileSubmitStarted(data));
    posthog.capture(`Onboarding Member Profile – Submit`, {
      ...helpers.objectDiff(defaultValues, data)
    });
  });
  const handlePreviewOpen = () => {
    setModalOpen(true);
  };
  const handlePreviewClose = () => {
    setModalOpen(false);
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    s: {
      px: 10,
      py: 6,
      mb: 10
    },
    collapsed: !open,
    elemAfter: success ? /*#__PURE__*/React.createElement(StyledIcon, {
      src: "checkboxFill",
      scale: 8,
      color: colors.success.main
    }) : undefined,
    shadow: true
  }, rest), error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Form, {
    onSubmit: onSubmit
  }, /*#__PURE__*/React.createElement(Grid, {
    cols: 2,
    template: "1fr 170px",
    gap: 5
  }, /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(ProfileOnboardingInfoForm, {
    control: control,
    setValue: setValue
  })), /*#__PURE__*/React.createElement(Right, null, /*#__PURE__*/React.createElement(ImageUploadControl, {
    initPreview: user.profileImage,
    name: "profileImage",
    control: control,
    label: t("MemberProfile.uploadImage"),
    s: {
      mt: 8
    },
    category: ImageCategoryEnum.PROFILE,
    border: "circled",
    width: "170px",
    height: "170px"
  }), /*#__PURE__*/React.createElement(Button, {
    fontSize: 3.5,
    s: {
      px: 5,
      py: 2,
      mt: 7
    },
    onClick: handlePreviewOpen,
    color: "translucentPrimary"
  }, t("MemberProfile.previewMemberCard")))), /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    gap: 5,
    justify: "end",
    s: {
      mt: 10,
      mb: 2
    },
    fullWidth: true
  }, statusIndex !== 0 && /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "arrow",
      scale: 5,
      rotate: 180,
      s: {
        mr: 2
      }
    }),
    color: "translucent",
    type: "submit",
    onClick: onBack,
    loading: backStatus === StatusStateEnum.LOADING,
    s: {
      pl: 4,
      pr: 6
    },
    rounded: true
  }, t("common.actions.back")), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    maxWidth: "230px",
    type: "submit",
    onClick: onSubmit,
    loading: nextStatus === StatusStateEnum.LOADING,
    fullWidth: true,
    rounded: true
  }, t("common.actions.save")))), /*#__PURE__*/React.createElement(Modal, {
    open: modalOpen
  }, /*#__PURE__*/React.createElement(Modal.Body, {
    onClose: handlePreviewClose,
    title: "Profile Preview",
    maxWidth: "360px",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Center, null, /*#__PURE__*/React.createElement(MemberCard, {
    color: "background",
    member: {
      ...user,
      ...watch()
    },
    maxWidth: "230px",
    fullWidth: true,
    s: {
      m: 4
    },
    noActions: true
  }), /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    maxWidth: "230px",
    onClick: handlePreviewClose,
    fullWidth: true,
    rounded: true
  }, t("common.actions.done"))))));
};
export default OnboardingMemberProfileCard;